import React, { useState, useEffect, useRef } from 'react';

const ImageCarousel = ({ images = [], containerClassName = '', imageClassName = 'object-cover', eagerIndex = 0 }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAutoPlay, setIsAutoPlay] = useState(true);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [translateX, setTranslateX] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const containerRef = useRef(null);

    // Default images from public folder (using placeholder images until you add your own)
    const defaultImages = [
        require('../assets/images/brush-rose.jpg'),
        require('../assets/images/brush-purple.jpg'),
        require('../assets/images/brush-green.jpg'),
    ];

    const displayImages = images.length > 0 ? images : defaultImages;

    // Get container width for calculations
    const getContainerWidth = () => {
        return containerRef.current?.offsetWidth || 300;
    };

    // Auto-rotate images
    useEffect(() => {
        if (!isAutoPlay || isDragging || isAnimating) return;

        const timer = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % displayImages.length);
        }, 5000); // Change image every 5 seconds

        return () => clearInterval(timer);
    }, [isAutoPlay, isDragging, isAnimating, displayImages.length]);

    // Handle drag/swipe start
    const handleDragStart = (clientX) => {
        if (isAnimating) return;
        setIsDragging(true);
        setStartX(clientX);
        setIsAutoPlay(false);
    };

    // Handle drag/swipe move
    const handleDragMove = (clientX) => {
        if (!isDragging || isAnimating) return;
        const diff = clientX - startX;
        setTranslateX(diff);
    };

    // Handle drag/swipe end
    const handleDragEnd = () => {
        if (!isDragging) return;
        setIsDragging(false);
        
        const containerWidth = getContainerWidth();
        const threshold = containerWidth * 0.15; // 15% of container width
        
        if (translateX > threshold) {
            // Swiped right - go to previous
            setIsAnimating(true);
            setTranslateX(containerWidth);
            setTimeout(() => {
                setCurrentIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length);
                setTranslateX(0);
                setIsAnimating(false);
            }, 300);
        } else if (translateX < -threshold) {
            // Swiped left - go to next
            setIsAnimating(true);
            setTranslateX(-containerWidth);
            setTimeout(() => {
                setCurrentIndex((prev) => (prev + 1) % displayImages.length);
                setTranslateX(0);
                setIsAnimating(false);
            }, 300);
        } else {
            // Snap back
            setTranslateX(0);
        }
        
        setTimeout(() => setIsAutoPlay(true), 10000); // Resume auto-play after 10 seconds
    };

    // Mouse events
    const handleMouseDown = (e) => {
        e.preventDefault();
        handleDragStart(e.clientX);
    };
    const handleMouseMove = (e) => handleDragMove(e.clientX);
    const handleMouseUp = () => handleDragEnd();
    const handleMouseLeave = () => {
        if (isDragging) handleDragEnd();
    };

    // Touch events
    const handleTouchStart = (e) => handleDragStart(e.touches[0].clientX);
    const handleTouchMove = (e) => handleDragMove(e.touches[0].clientX);
    const handleTouchEnd = () => handleDragEnd();

    // Calculate position for each image
    const getImageStyle = (index) => {
        const containerWidth = getContainerWidth();
        const prevIndex = (currentIndex - 1 + displayImages.length) % displayImages.length;
        const nextIndex = (currentIndex + 1) % displayImages.length;
        
        let baseTranslate = 0;
        let opacity = 0;
        let zIndex = 0;
        
        if (index === currentIndex) {
            baseTranslate = translateX;
            opacity = 1;
            zIndex = 2;
        } else if (index === prevIndex) {
            baseTranslate = -containerWidth + translateX;
            opacity = translateX > 0 ? 1 : 0;
            zIndex = translateX > 0 ? 1 : 0;
        } else if (index === nextIndex) {
            baseTranslate = containerWidth + translateX;
            opacity = translateX < 0 ? 1 : 0;
            zIndex = translateX < 0 ? 1 : 0;
        }
        
        return {
            transform: `translateX(${baseTranslate}px)`,
            opacity,
            zIndex,
            transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.3s ease'
        };
    };

    const prevIndex = (currentIndex - 1 + displayImages.length) % displayImages.length;
    const nextIndex = (currentIndex + 1) % displayImages.length;
    const visibleIndexes = [prevIndex, currentIndex, nextIndex];

    const defaultContainerClass = 'w-72 h-[420px] sm:w-80 sm:h-[460px] lg:w-96 lg:h-[520px]';
    const containerClasses = containerClassName || defaultContainerClass;

    return (
        <div className="relative w-full">
            {/* Main Image Container */}
            <div 
                ref={containerRef}
                className={`relative rounded-2xl overflow-hidden bg-white/60 shadow-none cursor-grab active:cursor-grabbing select-none ${containerClasses}`}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {/* Images */}
                {visibleIndexes.map((index) => {
                    const imageEntry = displayImages[index];
                    const imageData = typeof imageEntry === 'string' ? { src: imageEntry } : imageEntry;
                    const shouldEagerLoad = index === eagerIndex;
                    const imgProps = {
                        alt: imageData?.alt || `Product ${index + 1}`,
                        draggable: false,
                        loading: shouldEagerLoad ? 'eager' : 'lazy',
                        decoding: 'async',
                        fetchPriority: shouldEagerLoad ? 'high' : 'auto',
                        className: `absolute inset-0 w-full h-full ${imageClassName}`,
                        style: getImageStyle(index),
                        src: imageData?.src,
                        srcSet: imageData?.srcSet,
                        sizes: imageData?.sizes,
                        width: imageData?.width,
                        height: imageData?.height
                    };

                    if (imageData?.sources) {
                        return (
                            <picture key={index}>
                                {imageData.sources.avif && (
                                    <source type="image/avif" srcSet={imageData.sources.avif} sizes={imageData.sizes} />
                                )}
                                {imageData.sources.webp && (
                                    <source type="image/webp" srcSet={imageData.sources.webp} sizes={imageData.sizes} />
                                )}
                                <img {...imgProps} alt={imgProps.alt} />
                            </picture>
                        );
                    }

                    return <img key={index} {...imgProps} alt={imgProps.alt} />;
                })}
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-4">
                {displayImages.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => {
                            setCurrentIndex(index);
                            setIsAutoPlay(false);
                            setTimeout(() => setIsAutoPlay(true), 10000);
                        }}
                        className={`h-2 rounded-full transition-all ${
                            index === currentIndex
                                ? 'bg-[#E24C8B] w-6'
                                : 'bg-[#f3c0d6] hover:bg-[#e9a8c6] w-2'
                        }`}
                        aria-label={`Go to image ${index + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};

export default ImageCarousel;
